import { useMemo } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
// interfaces and object constants
import { Folder } from '@cube3/common/model/schema/resources/folder';
import { Library } from '@cube3/common/model/schema/resources/library';
import { Project } from '@cube3/common/model/schema/resources/project';
import { useResource__ALPHA } from '../useResource';
import { useResourceList__ALPHA } from '../useResourceList/useResourceList';

export const useVisitingFolder = ({ getAncestors = false }) => {
  /** Checks common router paths and, if successfull, returns the corresponding match with the relevant id  */

  const { pathname } = useLocation();

  const workspaceRouteMatch = useMemo(() => {
    // we do seperate matches, because react routers optional matching is a mess
    const folder = matchPath<{
      folderId: string;
      contextResourceType: 'library' | 'project';
      contextResourceId: string;
    }>(
      pathname,
      '/workspace/:ws/:contextResourceType/:contextResourceId/folder/:folderId'
    );

    if (folder) {
      return folder;
    }

    const context = matchPath<{
      folderId: string;
      contextResourceType: 'library' | 'project';
      contextResourceId: string;
    }>(pathname, '/workspace/:ws/:contextResourceType/:contextResourceId');

    if (
      !['project', 'library'].find(
        (c) => c === context?.params?.contextResourceType
      )
    ) {
      return;
    }

    return context;
  }, [pathname]);

  const activeFolderFromRoute = workspaceRouteMatch?.params?.folderId;

  const contextResourceType = workspaceRouteMatch?.params.contextResourceType;
  const contextResourceId = workspaceRouteMatch?.params.contextResourceId;

  const { resource: contextResource } = useResource__ALPHA<
    'project' | 'library'
  >({
    resourceId: contextResourceId,
    resourceType: contextResourceType
  });

  /** Attempts to get the current folder from the url or the root (contenttree) */

  // first we will try to find the current active folder, if available
  const visitingFolder =
    activeFolderFromRoute || contextResource?.relationships.node?.id;

  const { resources: ancestors } = useResourceList__ALPHA({
    resourceType: 'folder',
    resourceId: getAncestors && visitingFolder,
    edgeType: 'folder',
    edgeLabel: 'ancestors'
  });

  return useMemo(
    () => [visitingFolder, contextResource, ancestors],
    [visitingFolder, contextResource, ancestors]
  ) as [string?, (Library | Project)?, Folder[]?];
};
